import type {
  RouteConfig,
  RouteType,
  ExtractRouteMethods,
  FilterNever,
} from "./types";

// Internal registry structure
const registry: {
  protected: Record<string, RouteConfig[] | Record<string, RouteType>>;
  public: Record<string, RouteConfig[] | Record<string, RouteType>>;
} = {
  protected: {},
  public: {},
};

/**
 * Type helper to extract routes structure from a routes map
 * Filters out RouteConfig[] entries and extracts only Record<string, RouteType> routes
 * Used to automatically generate route types from const objects
 */
export type ExtractRoutesFromRegistry<
  T extends Record<string, RouteConfig[] | Record<string, RouteType>>
> = FilterNever<{
  [K in keyof T]: T[K] extends Record<string, RouteType>
    ? ExtractRouteMethods<T[K]>
    : never;
}>;

/**
 * Register a protected route group
 * @param name - Name of the route group (e.g., "product", "order")
 * @param routes - Route definitions (RouteConfig[] or Record<string, RouteType>)
 */
export function registerProtectedRoute(
  name: string,
  routes: RouteConfig[] | Record<string, RouteType>
): void {
  registry.protected[name] = routes;
}

/**
 * Register a public route group
 * @param name - Name of the route group (e.g., "auth")
 * @param routes - Route definitions (RouteConfig[] or Record<string, RouteType>)
 */
export function registerPublicRoute(
  name: string,
  routes: RouteConfig[] | Record<string, RouteType>
): void {
  registry.public[name] = routes;
}

/**
 * Get the complete route registry
 * @returns The route registry object
 */
export function getRegistry(): {
  protected: Record<string, RouteConfig[] | Record<string, RouteType>>;
  public: Record<string, RouteConfig[] | Record<string, RouteType>>;
} {
  return registry;
}
